import { ErrorObject } from 'ajv';
import { FormikValues } from 'formik';
import { RE_ENTER_ERROR_TYPE, RE_ENTER_PROPERTY_ID_PREFIX } from 'reEnter';
import { FormPage, FormPageProperties, PROPERTY_TYPE } from 'shared';
import { ajv } from '../ajv';
import { convertFormPageInputDataForValidation } from '../convertFormPageInputDataForValidation';

// FormikErrors
export type ValidationErrors = {
  [propertyId: string]: string[];
};

// ValidationErrors の値をエラーメッセージで返すかエラー種別で返すかを表す種別
export const ValidationErrorReturnType = {
  MESSAGE: 'message',
  ERROR_TYPE: 'errorType',
} as const;
type VALIDATION_ERROR_RETURN_TYPE =
  (typeof ValidationErrorReturnType)[keyof typeof ValidationErrorReturnType];

const isStringTruthy = (str: string | undefined): boolean =>
  str != null && str !== 'string';

const convertIntoString = (text?: string) =>
  (typeof text === 'string' && text) || '';

const getErrorsFormAjvResult = (
  ajvErrors?: ErrorObject[] | null,
  returnType?: VALIDATION_ERROR_RETURN_TYPE,
) => {
  if (!ajvErrors) {
    return {};
  }
  return ajvErrors.reduce((acc, error) => {
    const _propertyId = error.instancePath.split('/')[1];
    const propertyId =
      _propertyId ??
      error.params.errors?.[0].params.missingProperty ??
      error.params.missingProperty;
    const propertyCurrentErrors = acc[propertyId] || [];
    const errorType =
      error.keyword !== 'errorMessage'
        ? error.keyword
        : error.params.errors?.[0]?.keyword;
    const errorValue =
      returnType === ValidationErrorReturnType.MESSAGE
        ? error.message
        : errorType;
    return {
      ...acc,
      [propertyId]: [...propertyCurrentErrors, convertIntoString(errorValue)],
    };
  }, {} as ValidationErrors);
};

const getReEnterValueErrors = (
  formPage: FormPage,
  values: FormikValues,
  returnType?: VALIDATION_ERROR_RETURN_TYPE,
): ValidationErrors => {
  const { properties, required } = formPage;
  if (!properties) {
    return {};
  }
  const reEnterEntries = Object.entries(values).filter(([propertyId]) =>
    propertyId.startsWith(RE_ENTER_PROPERTY_ID_PREFIX),
  );
  return reEnterEntries.reduce((acc, [propertyId, value]) => {
    const originKey = propertyId.replace(RE_ENTER_PROPERTY_ID_PREFIX, '');
    const originValue = values[originKey];
    const originProperty = properties[originKey];
    if (
      originProperty.propertyType !== PROPERTY_TYPE.mailAddress &&
      originProperty.propertyType !== PROPERTY_TYPE.text &&
      originProperty.propertyType !== PROPERTY_TYPE.passwordHash
    ) {
      return acc;
    }

    // ValidationErrorReturnType.MESSAGE = {"__RE_ENTER_PROPERTY_ID_PREFIX__77799ea3-1f05-49b3-be84-45a94e2497fd":["再入力データと一致しません"]}
    // ValidationErrorReturnType.ERROR_TYPE = {"77799ea3-1f05-49b3-be84-45a94e2497fd":["reEnter"]}
    const reEnterErrorValue =
      returnType === ValidationErrorReturnType.MESSAGE
        ? originProperty.reEnter?.errorMessage
        : RE_ENTER_ERROR_TYPE;
    const reErrorMessage = convertIntoString(reEnterErrorValue);
    const reEnterPropertyId =
      returnType === ValidationErrorReturnType.MESSAGE ? propertyId : originKey;
    const isRequired = required?.includes(originKey) ?? false;
    if (isStringTruthy(originValue)) {
      return originValue === value
        ? acc
        : {
            ...acc,
            [reEnterPropertyId]: [reErrorMessage],
          };
    }
    if (isStringTruthy(value)) {
      return {
        ...acc,
        [reEnterPropertyId]: [reErrorMessage],
      };
    }

    // (value === '' || value == null)
    //    && (originValue === '' || originValue == null)
    // 必須項目で両方とも未入力のケース = アクセスレポートではエラー計上しない
    if (isRequired && returnType === ValidationErrorReturnType.MESSAGE) {
      return {
        ...acc,
        [reEnterPropertyId]: [reErrorMessage],
      };
    }
    return acc;
  }, {} as ValidationErrors);
};

export const validator =
  (
    formPage: FormPage,
    returnType: VALIDATION_ERROR_RETURN_TYPE = ValidationErrorReturnType.MESSAGE,
  ) =>
  (values: FormikValues): ValidationErrors => {
    const valuesForValidation = convertFormPageInputDataForValidation(
      formPage.properties as FormPageProperties,
      values,
    );
    ajv.validate(formPage, valuesForValidation);
    const errorsFromAjv = getErrorsFormAjvResult(ajv.errors, returnType);
    const errorsFromReEnter = getReEnterValueErrors(
      formPage,
      valuesForValidation,
      returnType,
    );
    const mergedErrors = Object.keys({
      ...errorsFromAjv,
      ...errorsFromReEnter,
    }).reduce(
      (acc, propertyId) => {
        acc[propertyId] = [
          ...(errorsFromAjv[propertyId] ? errorsFromAjv[propertyId] : []),
          ...(errorsFromReEnter[propertyId]
            ? errorsFromReEnter[propertyId]
            : []),
        ];
        return acc;
      },
      {} as Record<string, string[]>,
    );
    return mergedErrors;
  };
